import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { HubConnection, HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

type RefreshListListener = () => void;
type RefreshJobListener = (jobId: string) => void;
type ProgressPayload = {
  jobId: string;
  batchId: string;
  progress: number;
};
type ProgressListener = (payload: ProgressPayload) => void;

const refreshListListeners = new Set<RefreshListListener>();
const refreshJobListeners = new Set<RefreshJobListener>();
const progressListeners = new Set<ProgressListener>();

let jobListSubscriptions = 0;
const jobSubscriptions = new Map<string, number>();

let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;

const getConnectionUrl = () => runtimeConfig.API_BASE_URL.replace("/swagger", "") + API_ENDPOINTS.jobsHub;

const dispatchRefreshList = () => {
  refreshListListeners.forEach((listener) => listener());
};

const dispatchRefreshJob = (payload?: { jobId?: string }) => {
  if (!payload?.jobId) return;
  refreshJobListeners.forEach((listener) => listener(payload.jobId!));
};

const dispatchProgress = (payload?: Partial<ProgressPayload>) => {
  if (!payload?.jobId || !payload.batchId || payload.progress == null) return;

  const normalizedPayload: ProgressPayload = {
    jobId: payload.jobId,
    batchId: payload.batchId,
    progress: payload.progress,
  };

  progressListeners.forEach((listener) => listener(normalizedPayload));
};

const getOrCreateConnection = () => {
  if (connection) {
    return connection;
  }

  connection = new HubConnectionBuilder()
    .withUrl(getConnectionUrl(), {
      transport: HttpTransportType.ServerSentEvents,
    })
    .withAutomaticReconnect()
    .build();

  connection.on("RefreshList", dispatchRefreshList);
  connection.on("RefreshJob", dispatchRefreshJob);
  connection.on("ReceiveProgress", dispatchProgress);

  connection.onreconnected(() => resubscribe());
  connection.onclose(() => {
    startPromise = null;
  });

  return connection;
};

const startConnection = async () => {
  const currentConnection = getOrCreateConnection();

  if (currentConnection.state === "Connected") {
    return;
  }

  if (startPromise) {
    return startPromise;
  }

  startPromise = currentConnection.start().then(() => {
    startPromise = null;
  }).catch((error) => {
    startPromise = null;
    console.error("Jobs hub connection error:", error);
    throw error;
  });

  return startPromise;
};

const invokeWhenConnected = async (methodName: string, ...args: unknown[]) => {
  await startConnection();
  await getOrCreateConnection().invoke(methodName, ...args);
};

const resubscribe = async () => {
  if (jobListSubscriptions > 0) {
    await invokeWhenConnected("SubscribeToJobList");
  }

  for (const jobId of jobSubscriptions.keys()) {
    await invokeWhenConnected("SubscribeToJob", jobId);
  }
};

const stopConnectionIfIdle = () => {
  if (refreshListListeners.size > 0 || refreshJobListeners.size > 0 || progressListeners.size > 0 || jobListSubscriptions > 0 || jobSubscriptions.size > 0) {
    return;
  }

  const currentConnection = connection;
  connection = null;
  startPromise = null;

  if (currentConnection) {
    currentConnection.off("RefreshList", dispatchRefreshList);
    currentConnection.off("RefreshJob", dispatchRefreshJob);
    currentConnection.off("ReceiveProgress", dispatchProgress);
    void currentConnection.stop().catch((error) => {
      console.error("Error stopping jobs hub connection:", error);
    });
  }
};

export const subscribeToJobsList = () => {
  jobListSubscriptions += 1;
  if (jobListSubscriptions === 1) {
    void invokeWhenConnected("SubscribeToJobList").catch((error) => {
      console.error("Error subscribing to jobs list:", error);
    });
  }

  return () => {
    jobListSubscriptions = Math.max(0, jobListSubscriptions - 1);
    if (jobListSubscriptions === 0) {
      void invokeWhenConnected("UnsubscribeFromJobList").catch((error) => {
        console.error("Error unsubscribing from jobs list:", error);
      });
    }

    stopConnectionIfIdle();
  };
};

export const subscribeToJob = (jobId: string) => {
  const nextCount = (jobSubscriptions.get(jobId) ?? 0) + 1;
  jobSubscriptions.set(jobId, nextCount);

  if (nextCount === 1) {
    void invokeWhenConnected("SubscribeToJob", jobId).catch((error) => {
      console.error(`Error subscribing to job ${jobId}:`, error);
    });
  }

  return () => {
    const currentCount = jobSubscriptions.get(jobId) ?? 0;

    if (currentCount <= 1) {
      jobSubscriptions.delete(jobId);
      void invokeWhenConnected("UnsubscribeFromJob", jobId).catch((error) => {
        console.error(`Error unsubscribing from job ${jobId}:`, error);
      });
    } else {
      jobSubscriptions.set(jobId, currentCount - 1);
    }

    stopConnectionIfIdle();
  };
};

export const subscribeToRefreshList = (listener: RefreshListListener) => {
  refreshListListeners.add(listener);
  void startConnection().catch(() => undefined);

  return () => {
    refreshListListeners.delete(listener);
    stopConnectionIfIdle();
  };
};

export const subscribeToRefreshJob = (listener: RefreshJobListener) => {
  refreshJobListeners.add(listener);
  void startConnection().catch(() => undefined);

  return () => {
    refreshJobListeners.delete(listener);
    stopConnectionIfIdle();
  };
};

export const subscribeToProgress = (listener: ProgressListener) => {
  progressListeners.add(listener);
  void startConnection().catch(() => undefined);

  return () => {
    progressListeners.delete(listener);
    stopConnectionIfIdle();
  };
};
